(function () {
  'use strict';
  const core=window.GlycoraCore;
  if (!core) return;
  const RATE_TTL=6*60*60*1000;
  const MAX_STALE=48*60*60*1000;

  async function fetchJson(url, options={}) {
    const controller=new AbortController();
    const timer=setTimeout(()=>controller.abort(),10000);
    try {
      const response=await fetch(url,{credentials:'same-origin',...options,signal:controller.signal});
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  async function exchangeRates(currencies) {
    const quotes=[...new Set(currencies)].filter(c=>c!=='EUR').sort();
    if (!quotes.length) return {EUR:1};
    if (!quotes.every(c=>/^[A-Z]{3}$/.test(c))) throw new Error('Invalid currency');
    const key=`glycora.fx.v2.${quotes.join('.')}`;
    let cached;
    try { cached=JSON.parse(localStorage.getItem(key)); } catch { /* Storage can be unavailable. */ }
    const valid=entry=>entry && Number.isFinite(entry.savedAt) && entry.savedAt<=Date.now() && quotes.every(c=>Number.isFinite(entry.rates?.[c])&&entry.rates[c]>0);
    if (valid(cached)&&Date.now()-cached.savedAt<RATE_TTL) return cached.rates;
    try {
      const rows=await fetchJson(`https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${quotes.join(',')}&providers=ECB`,{credentials:'omit'});
      const rates={EUR:1};
      for (const row of rows) if (row.base==='EUR' && quotes.includes(row.quote) && Number.isFinite(row.rate) && row.rate>0) rates[row.quote]=row.rate;
      const entry={savedAt:Date.now(),rates};
      if (!valid(entry)) throw new Error('Incomplete exchange rates');
      try { localStorage.setItem(key,JSON.stringify(entry)); } catch { /* Continue without cache. */ }
      return rates;
    } catch (error) {
      if (valid(cached)&&Date.now()-cached.savedAt<MAX_STALE) return cached.rates;
      throw error;
    }
  }

  async function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized='true';
    const config=JSON.parse(root.querySelector('[data-glycora-config]').textContent);
    const fallback=JSON.parse(root.querySelector('[data-glycora-fallback]').textContent);
    let data=fallback;
    let strings=fallback.strings;
    let translations={};
    let market=core.detectMarket(location.pathname,fallback);
    let variant=null;
    let busy=false;
    let alive=true;
    let requestVersion=0;
    const disposers=[];
    const status=root.querySelector('[data-price-status]');
    const retry=root.querySelector('[data-price-retry]');
    const checkoutButtons=[...root.querySelectorAll('[data-checkout]')];
    const text=(key,params={})=>core.interpolate(translations[key] || strings[key] || '',{min:market.deliveryDays[0],max:market.deliveryDays[1],...params});
    const on=(el,event,fn,options)=>{el.addEventListener(event,fn,options);disposers.push(()=>el.removeEventListener(event,fn,options));};

    function applyTranslations() {
      const walker=document.createTreeWalker(root,NodeFilter.SHOW_COMMENT);
      while(walker.nextNode()) {
        const comment=walker.currentNode;
        if (!comment.data.startsWith('glycora:')) continue;
        const key=comment.data.slice(8), node=comment.nextSibling;
        if (node?.nodeType===Node.TEXT_NODE && (translations[key] || strings[key])) node.nodeValue=text(key);
      }
      for (const attribute of ['alt','aria-label','aria-roledescription']) {
        root.querySelectorAll(`[data-i18n-${attribute}]`).forEach(el=>{
          const key=el.getAttribute(`data-i18n-${attribute}`);
          if (translations[key] || strings[key]) el.setAttribute(attribute,text(key));
        });
      }
      root.querySelectorAll('[data-review-name]').forEach(el=>{el.textContent=market.names[Number(el.dataset.reviewName)];});
      root.querySelectorAll('[data-review-alt]').forEach(el=>{el.alt=market.names[Number(el.dataset.reviewAlt)];});
      // Untranslated markets retain an English document language until their dictionary is populated.
      document.documentElement.lang=Object.keys(translations).length?market.language:'en';
      root.lang=document.documentElement.lang;
      document.title=text('meta.title');
      document.querySelector('meta[name="description"]')?.setAttribute('content',text('meta.description'));
    }

    async function loadLocale() {
      try {
        const response=await fetchJson(config.localesUrl);
        if (response.schemaVersion!==1 || !response.markets || !response.locales) throw new Error('Invalid locale data');
        data=response;
        market=core.detectMarket(location.pathname,data);
        strings={...fallback.strings,...data.reference};
        translations=data.locales[market.language] || {};
        root.dataset.localeStatus='loaded';
      } catch {
        root.dataset.localeStatus='fallback';
      }
      if(alive) applyTranslations();
    }

    function enableButtons() {
      for (const button of checkoutButtons) {
        const bundle=button.dataset.checkout;
        const enabled=bundle==='single' || config[`${bundle}Enabled`]===true;
        button.disabled=busy || !enabled || !core.canOrder(variant,core.bundles[bundle].quantity);
        button.setAttribute('aria-disabled',String(button.disabled));
      }
    }

    async function loadPrices() {
      const version=++requestVersion;
      variant=null;
      enableButtons();
      status.textContent=text('price.loading');
      retry.hidden=true;
      root.dataset.priceStatus='loading';
      try {
        if (!/^[a-z0-9][a-z0-9-]*$/.test(config.productHandle)) throw new Error('Invalid product handle');
        const rootPath=(config.rootUrl || '/').replace(/\/?$/,'/');
        const [product,cart]=await Promise.all([
          fetchJson(`${rootPath}products/${encodeURIComponent(config.productHandle)}.js`,{cache:'no-store'}),
          fetchJson(`${rootPath}cart.js`,{cache:'no-store'})
        ]);
        const selected=core.chooseVariant(product,new URLSearchParams(location.search).get('variant'));
        if (!selected?.available || selected.requires_selling_plan) {
          if (!alive || version!==requestVersion) return;
          status.textContent=text('price.unavailable');
          root.dataset.priceStatus='unavailable';
          return;
        }
        // Ajax money and cart.currency belong to the same Shopify Markets session.
        // Convert from that source currency, never relabel a contextual price as EUR.
        const currency=cart.currency;
        const rates=await exchangeRates([currency,market.currency]);
        const prices=core.calculatePrices(selected.price,currency,market.currency,rates,config.shippingEurCents);
        if (!alive || version!==requestVersion) return;
        const formatter=new Intl.NumberFormat(market.locale,{style:'currency',currency:market.currency,minimumFractionDigits:2,maximumFractionDigits:2});
        const amount=key=>{
          const [bundle,field]=key.split('.');
          return formatter.format(prices[bundle][field]/100);
        };
        root.querySelectorAll('[data-money]').forEach(el=>{el.textContent=amount(el.dataset.money);});
        root.querySelectorAll('[data-money-text]').forEach(el=>{el.textContent=text(el.dataset.moneyFormat,{amount:amount(el.dataset.moneyText)});});
        variant=selected;
        status.textContent='';
        root.dataset.priceStatus='ready';
        root.dataset.currency=market.currency;
        root.dataset.sourceCurrency=currency;
        enableButtons();
      } catch {
        if (!alive || version!==requestVersion) return;
        status.textContent=text('price.error');
        retry.hidden=false;
        root.dataset.priceStatus='error';
        enableButtons();
      }
    }

    checkoutButtons.forEach(button=>on(button,'click',async()=>{
      if (busy || button.disabled || !variant) return;
      busy=true;
      enableButtons();
      status.textContent=text('checkout.busy');
      try {
        const bundle=button.dataset.checkout;
        const rootPath=(config.rootUrl || '/').replace(/\/?$/,'/');
        const product=await fetchJson(`${rootPath}products/${encodeURIComponent(config.productHandle)}.js`,{cache:'no-store'});
        const current=core.chooseVariant(product,variant.id);
        if (!core.canOrder(current,core.bundles[bundle].quantity)) throw new Error('Unavailable variant');
        // Refresh displayed amounts if the merchant changed the product while the page was open.
        if (current.price!==variant.price) {
          busy=false;
          await loadPrices();
          return;
        }
        const path=core.checkoutPath(config.rootUrl,current.id,bundle,market,config[`${bundle}Discount`] || '');
        location.assign(path);
      } catch {
        busy=false;
        enableButtons();
        status.textContent=text('checkout.error');
      }
    }));
    on(retry,'click',async()=>{await loadLocale();await loadPrices();});
    on(window,'pageshow',()=>{busy=false;enableButtons();});

    const mobile=root.querySelector('.mobile-purchase'), hero=root.querySelector('.hero'), offers=root.querySelector('#paketi');
    if (mobile && hero && offers && 'IntersectionObserver' in window) {
      let heroVisible=true, offersVisible=false;
      const observer=new IntersectionObserver(entries=>{
        for(const entry of entries) {
          if(entry.target===hero) heroVisible=entry.isIntersecting;
          if(entry.target===offers) offersVisible=entry.isIntersecting;
        }
        const visible=!heroVisible&&!offersVisible;
        mobile.classList.toggle('visible',visible);
        mobile.inert=!visible;
      });
      mobile.inert=true;
      observer.observe(hero);observer.observe(offers);
      disposers.push(()=>observer.disconnect());
    }
    root.querySelector('#year').textContent=String(new Date().getFullYear());
    root.querySelectorAll('[data-carousel]').forEach(carousel=>{
      const track=carousel.querySelector('[data-carousel-track]');
      const slides=[...track.querySelectorAll('[data-carousel-slide]')];
      const previous=carousel.querySelector('.previous'), next=carousel.querySelector('.next'), position=carousel.querySelector('.carousel-position');
      let timer;
      const update=()=>{
        const bounds=track.getBoundingClientRect();
        const visible=slides.flatMap((slide,index)=>{
          const r=slide.getBoundingClientRect();
          return Math.max(0,Math.min(r.right,bounds.right)-Math.max(r.left,bounds.left))/r.width>.5?[index+1]:[];
        });
        previous.disabled=track.scrollLeft<2;
        next.disabled=track.scrollLeft>=track.scrollWidth-track.clientWidth-2;
        if(visible.length) position.textContent=`${visible[0]}${visible.at(-1)===visible[0]?'':'–'+visible.at(-1)} / ${slides.length}`;
      };
      const go=direction=>{
        const left=track.getBoundingClientRect().left;
        const stops=slides.map(slide=>slide.getBoundingClientRect().left-left+track.scrollLeft);
        const current=stops.reduce((best,offset,index)=>Math.abs(offset-track.scrollLeft)<Math.abs(stops[best]-track.scrollLeft)?index:best,0);
        const max=track.scrollWidth-track.clientWidth;
        const target=direction==='start'?0:direction==='end'?max:stops[Math.max(0,Math.min(slides.length-1,current+direction))];
        track.scrollTo({left:Math.max(0,Math.min(max,target)),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      };
      on(previous,'click',()=>go(-1));on(next,'click',()=>go(1));
      on(track,'keydown',event=>{
        const actions={ArrowLeft:-1,ArrowRight:1,Home:'start',End:'end'};
        if(event.target===track && event.key in actions){event.preventDefault();go(actions[event.key]);}
      });
      on(track,'scroll',()=>{clearTimeout(timer);timer=setTimeout(update,90);},{passive:true});
      if('ResizeObserver' in window){const resize=new ResizeObserver(update);resize.observe(track);disposers.push(()=>resize.disconnect());}
      disposers.push(()=>clearTimeout(timer));
      update();
    });
    const composition=root.querySelector('#formula');
    const reveal=()=>{if(location.hash==='#formula'&&composition) composition.open=true;};
    root.querySelectorAll('a[href="#formula"]').forEach(link=>on(link,'click',()=>{composition.open=true;}));
    on(window,'hashchange',reveal);reveal();
    root.glycoraCleanup=()=>{alive=false;disposers.forEach(dispose=>dispose());};
    await loadLocale();
    await loadPrices();
  }
  const boot=scope=>scope.querySelectorAll('[data-glycora-landing]').forEach(root=>init(root).catch(()=>{root.dataset.priceStatus='error';}));
  boot(document);
  document.addEventListener('shopify:section:load',event=>boot(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelector('[data-glycora-landing]')?.glycoraCleanup?.());
})();
